import React, { Component } from 'react';
import * as Yup from 'yup';
import { withFormik, Field } from 'formik';
import { get } from 'lodash';
import { Form, Button } from 'semantic-ui-react';
import { withLocalize } from 'react-localize-redux';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { HHInput, HHFileUploader, HHRoleSelector } from './fields';
import { FORM_NAME } from '../forms/formConstant';
import {
  fetchUserProfile,
  fetchTags,
  updateUserProfile,
  updateUserSocialMedia,
  setSignupRole
} from '../actions';
import { alphaNumericRegex } from './regex';
import Avatar from '../common/avatar';
import { hlog } from '../common/util';

// TODO validate username if existed
const formikEnhancer = withFormik({
  enableReinitialize: true,
  validationSchema: ({ translate, user }) => {
    const validationShape = {
      username: Yup.string()
        .required(translate('form.error.mixed.required'))
        .matches(alphaNumericRegex, {
          excludeEmptyString: true,
          message: translate('form.error.regex.alphaNumeric')
        }),
      avatarUrl: Yup.string().url(translate('form.error.string.url')),
      signupRole: Yup.string()
        .required(translate('form.error.mixed.required'))
    };

    if (!user.profile) {
      validationShape.firstName = Yup.string()
        .max(30, translate('form.error.string.max'))
        .required(translate('form.error.mixed.required'));
      validationShape.lastName = Yup.string()
        .max(30, translate('form.error.string.max'))
        .required(translate('form.error.mixed.required'));
    }

    return Yup.object().shape(validationShape);
  },
  mapPropsToValues: ({ user }) => ({
    avatarUrl: get(user, 'profile.basicInfo.avatarUrl', ''),
    firstName: user.thirdPartyFirstName || '',
    lastName: user.thirdPartyLastName || '',
    username:
      // eslint-disable-next-line no-nested-ternary
      user.profile ?
        get(user, 'profile.basicInfo.username', '') :
        user.thirdPartyUsername ?
          user.thirdPartyUsername : '',
    signupRole: 'Participant',
    isFromSignup: true
  }),
  handleSubmit: (values, { props, setSubmitting }) => {
    const payload = { ...values };
    if (props.user.profile) {
      delete payload.firstName;
      delete payload.lastName;
    } else {
      payload.email = props.user.thirdPartyEmail;
      payload.thirdPartyType = props.user.thirdPartyFrom;
      payload.thirdPartyIdentification = props.user.thirdPartyIdentification;
    }

    if (props.user.thirdPartyFrom && props.user.thirdPartyFrom === 'Github') {
      props.updateUserSocialMedia({ github: props.user.githubUsername }, () => { hlog('called'); });
    }

    props.setSignupRole(values.signupRole);

    props
      .updateUserProfile(payload, () => {
        if (values.signupRole === 'Organizer') {
          props.history.push('/admin/create-event');
        } else if (values.signupRole === 'Participant' || values.signupRole === 'Others') {
          props.history.push('/complete-profile');
        } else {
          props.history.push('/');
        }
      })
      .then(() => {
        setSubmitting(false);
      });
  },
  displayName: FORM_NAME.USER_BASIC_PROFILE_FORM
});

class SignupBasicForm extends Component {
  async componentDidMount() {
    const { user, fetchUserProfile, history } = this.props;
    if (user.signupRole === 'Organizer') {
      history.push('/admin/create-event');
    } else if (user.signupRole === 'Participant' || user.signupRole === 'Others') {
      history.push('/complete-profile');
    } else if (user.token) {
      await fetchUserProfile();
    }
  }

  setRoleOptions = () => ([
    {
      caption: this.props.translate('common.participant'),
      value: 'Participant'
    },
    {
      caption: this.props.translate('common.judge'),
      value: 'Judge'
    },
    {
      caption: this.props.translate('common.mentor'),
      value: 'Mentor'
    },
    {
      caption: this.props.translate('common.organizer'),
      value: 'Organizer'
    },
    {
      caption: this.props.translate('common.volunteer'),
      value: 'Volunteer'
    },
    {
      caption: this.props.translate('common.others'),
      value: 'Others'
    }
  ]);

  renderUserAvatar = (values, user) => (
    <Avatar
      avatarUrl={values.avatarUrl}
      user={user.profile}
      className='profile_info_basic_img'
      fontSize='3rem'
    />
  )

  render() {
    const {
      handleSubmit, isSubmitting, translate, values, user
    } = this.props;

    return (
      <Form onSubmit={handleSubmit}>
        <div className='signupFormTitle'>
          {translate('form.hint.signupBasicAvatar')}
        </div>
        <Form.Field className='completeProfile_avatar'>
          <Field
            name='avatarUrl'
            component={HHFileUploader}
            otherProps={{
              type: 'image_1x1',
              trigger: this.renderUserAvatar(values, user)
            }}
          />
        </Form.Field>
        { user.profile ?
          <div className='signupFormTitle'>
            {translate('form.hint.signupBasicUsername')}
          </div> :
          <div className='signupNameContainer'>
            <Form.Field >
              <Field
                name='firstName'
                component={HHInput}
                otherProps={{
                  id: 'firstName',
                  className: 'g-rounded',
                  placeholder: translate('form.placeholder.firstName'),
                  type: 'text'
                }}
              />
            </Form.Field>
            <Form.Field>
              <Field
                name='lastName'
                component={HHInput}
                otherProps={{
                  id: 'lastName',
                  className: 'g-rounded',
                  placeholder: translate('form.placeholder.lastName'),
                  type: 'text'
                }}
              />
            </Form.Field>
          </div>
        }
        <Form.Field>
          <Field
            name='username'
            component={HHInput}
            otherProps={{
              id: 'username',
              type: 'text',
              className: 'g-rounded',
              placeholder: translate('valid username explaination')
            }}
          />
        </Form.Field>
        <div className='signupFormTitle'>
          {translate('form.hint.signupBasicRole')}
        </div>
        <Form.Field>
          <Field
            name='signupRole'
            component={HHRoleSelector}
            otherProps={{
              id: 'signupRole',
              options: this.setRoleOptions()
            }}
          />
        </Form.Field>
        <Button
          fluid
          primary
          loading={isSubmitting}
          disabled={isSubmitting}
          className='g-rounded g-gradient'
          type='submit'
          size='medium'
        >
          {translate('done')}
        </Button>
      </Form>
    );
  }
}

export default compose(
  withLocalize,
  connect(
    (({ user }) => ({ user })),
    {
      fetchTags,
      fetchUserProfile,
      updateUserProfile,
      updateUserSocialMedia,
      setSignupRole
    }
  ),
  formikEnhancer
)(SignupBasicForm);



// WEBPACK FOOTER //
// ./src/components/forms/signupBasicForm.js